import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqs } from '@/mocks/categories';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    setFormStatus('idle');

    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9phkimb4qjhggaepukg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;

      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const milestones = [
    { year: '2022', title: '平台创立', desc: '17.c-起草口 正式上线，以商务文件起草为核心切入点，开启专业文案起草服务之路。' },
    { year: '2023', title: '品类扩展', desc: '新增创意文案、法律文书、行政公文三大品类，服务覆盖面扩展至全场景起草需求。' },
    { year: '2024', title: '用户突破', desc: '累计服务用户突破 50,000 人，起草文档总数超过 120,000 份，用户满意度达 98.5%。' },
    { year: '2025', title: '内容深耕', desc: '引入资深行业专家审核机制，学术论文与个人文书品类上线，持续提升内容专业度。' },
    { year: '2026', title: '持续进化', desc: '每周内容更新，六大品类全面覆盖，致力于成为最值得信赖的专业起草平台。' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 17.c-起草口 | 专业文案起草平台"
        description="17.c-起草口 是专业的在线文案起草平台，提供一站式草稿撰写、内容规划与文件起草服务。了解我们的品牌故事、发展历程与联系方式。"
        keywords="17.c-起草口,关于我们,起草平台介绍,品牌故事,联系方式"
        canonical="/about"
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于 17.c-起草口',
          description: '了解 17.c-起草口的品牌故事、发展历程与联系方式。',
          url: 'https://zhimycloud.cn/about',
        }}
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zhimycloud.cn/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://zhimycloud.cn/about' },
          ],
        }}
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '17.c-起草口',
          url: 'https://zhimycloud.cn',
          description: '专业的在线文案起草平台，提供一站式草稿撰写、内容规划与文件起草服务。',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '建国路88号',
            addressLocality: '北京',
            addressRegion: '朝阳区',
            addressCountry: 'CN',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '400-888-1717',
            email: 'contact@zhimycloud.cn',
            contactType: 'customer service',
            availableLanguage: 'Chinese',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Brand Intro */}
        <section className="bg-background-100 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
              关于 17.c-起草口
            </h1>
            <div className="bg-background-50 rounded-lg p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground-950 mb-4">我们的使命</h2>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    17.c-起草口 诞生于一个简单的信念：每个人都应该能够轻松完成专业的文案起草。
                  </p>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    在日常工作和生活中，无论是商务合同、法律文书还是创意文案，专业的起草往往需要耗费大量的时间和精力，甚至需要依赖外部专业人士。17.c-起草口 的使命就是将专业的起草知识系统化、结构化，让每一位用户都能快速掌握起草要领，高效完成每一份文档。
                  </p>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    我们汇聚了来自法律、商务、行政、学术和创意领域的资深专业人士，将他们的经验和知识转化为清晰易懂的起草指南。每一篇内容都经过严格的审核流程，确保其专业性和实用性。
                  </p>
                </div>
                <div className="w-full md:w-64 shrink-0">
                  <div className="bg-accent-100 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold text-accent-600 mb-1">50,000+</div>
                    <div className="text-foreground-600 text-sm">累计服务用户</div>
                    <div className="mt-3 text-3xl font-bold text-accent-600 mb-1">120,000+</div>
                    <div className="text-foreground-600 text-sm">起草文档数</div>
                    <div className="mt-3 text-3xl font-bold text-accent-600 mb-1">98.5%</div>
                    <div className="text-foreground-600 text-sm">用户满意度</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 transform md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative flex flex-col md:flex-row gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                      <div className={`${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="hidden md:block">
                          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-bold mb-2">{m.year}</span>
                          <h3 className="text-foreground-950 font-semibold text-base mb-1.5">{m.title}</h3>
                          <p className="text-foreground-600 text-sm leading-relaxed">{m.desc}</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-3.5 md:left-1/2 top-1 w-3 h-3 bg-accent-500 rounded-full transform -translate-x-1/2"></div>
                    <div className="md:w-1/2 md:px-8 md:block hidden">
                      <div className={`${i % 2 !== 0 ? 'md:text-right' : 'md:text-left'}`}>
                        {/* spacer for timeline layout */}
                      </div>
                    </div>
                    <div className="pl-10 md:hidden">
                      <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-bold mb-2">{m.year}</span>
                      <h3 className="text-foreground-950 font-semibold text-base mb-1.5">{m.title}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: 'ri-shield-star-line', title: '专业至上', desc: '所有内容经由行业专家严格审核，确保每一篇起草指南都符合专业标准和行业规范。' },
                { icon: 'ri-user-heart-line', title: '用户为本', desc: '从用户实际需求出发设计内容结构，让每个起草场景都能找到对应的解决方案。' },
                { icon: 'ri-loop-left-line', title: '持续精进', desc: '紧跟行业趋势和法规变化，持续更新内容，确保用户获取的知识始终处于前沿。' },
              ].map((v) => (
                <div key={v.title} className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto bg-accent-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-950 font-semibold text-base mb-2">{v.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 md:py-24">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 text-center">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">
              如有任何问题或合作意向，请通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@zhimycloud.cn' },
                { icon: 'ri-phone-line', label: '客服电话', value: '400-888-1717' },
                { icon: 'ri-map-pin-line', label: '办公地址', value: '北京市朝阳区建国路88号' },
              ].map((c) => (
                <div key={c.label} className="bg-background-100 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 mx-auto bg-accent-100 rounded-lg flex items-center justify-center mb-3">
                    <i className={`${c.icon} text-accent-600`}></i>
                  </div>
                  <div className="text-foreground-500 text-xs mb-1">{c.label}</div>
                  <div className="text-foreground-950 text-sm font-medium">{c.value}</div>
                </div>
              ))}
            </div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6 md:p-8"
            >
              <h3 className="text-lg font-bold text-foreground-950 mb-6">发送消息</h3>

              {formStatus === 'success' && (
                <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-foreground-700 text-sm mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-foreground-700 text-sm mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-foreground-700 text-sm mb-1.5">主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-foreground-700 text-sm mb-1.5">消息内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors resize-none"
                  placeholder="请输入您的消息内容（不超过500字）"
                ></textarea>
                <div className="text-foreground-400 text-xs mt-1 text-right">最多500字</div>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
                style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitting ? '发送中...' : '发送消息'}
              </button>
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}